<script>
import { storeToRefs } from "pinia";
import { computed, defineComponent } from "vue";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import { formatName } from "@/common/utils";
import ClassQuizBaseLayout from "@/components/vocabularyTrainer/ClassQuizBaseLayout.vue";
import ClassQuizLobbyPrompt from "@/components/vocabularyTrainer/ClassQuizLobbyPrompt.vue";
import QuizJumbotron from "@/components/vocabularyTrainer/QuizJumbotron.vue";
import { i18n } from "@/i18n";
import { useAuthStore } from "@/stores";

import { useClassQuizComponent } from "./use-class-quiz-component";

const { commonProps, commonEmits } = useClassQuizComponent();

export default defineComponent({
  components: { ClassQuizBaseLayout, ClassQuizLobbyPrompt, QuizJumbotron, TgButton },
  props: commonProps,
  emits: commonEmits,
  setup(props, { emit }) {
    // Import globals
    const { t } = i18n.global;
    const authStore = useAuthStore();

    const { userIsTeacher } = storeToRefs(authStore);

    // Template variables/functions
    const listName = computed(() => props.classQuiz.listName ?? "");
    const isAwaitingPlayers = computed(() => props.classQuiz.players.length === 0);
    const players = computed(() => props.classQuiz.players.map((p) => formatName(p.name)));
    const onStartQuiz = () => emit("startQuiz");

    return {
      t,

      listName,
      isAwaitingPlayers,

      userIsTeacher,
      players,
      onStartQuiz
    };
  }
});
</script>

<template>
  <ClassQuizBaseLayout>
    <template #jumbotron>
      <QuizJumbotron>
        <div class="container">
          <ClassQuizLobbyPrompt :list-name="listName" />
        </div>
      </QuizJumbotron>
    </template>

    <div class="class-quiz-lobby-body">
      <p v-if="isAwaitingPlayers" class="class-quiz-lobby-waiting-text">
        {{ t("vocab.classQuiz.lobby.awaitingPlayers") }}
      </p>
      <div v-else class="class-quiz-lobby-players-container">
        <span
          v-for="(player, index) in players"
          :key="index"
          class="class-quiz-lobby-players-container__player"
        >
          {{ player }}
        </span>
      </div>
    </div>

    <template v-if="userIsTeacher" #footer>
      <div class="container text-center">
        <TgButton
          size="md"
          size-md="lg"
          :disabled="classQuiz.players.length === 0"
          @click="onStartQuiz()"
        >
          {{ t("vocab.classQuiz.lobby.start") }}
        </TgButton>
      </div>
    </template>
  </ClassQuizBaseLayout>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.class-quiz-lobby-body {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
}
.class-quiz-lobby-waiting-text {
  margin-bottom: 0;
  color: rgb(@theme-colors[@grey][base]);
  font-size: 1.5rem;
  font-weight: 500;
  line-height: 1.75rem;
  text-align: center;
}

.class-quiz-lobby-players-container {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;

  #media-breakpoint-up(md, {
    max-width: 50%;
    gap: 30px;
  });

  &__player {
    color: rgb(@theme-colors[@grey][110]);
    font-size: 1.5rem;
    font-weight: 500;
    line-height: 1.75rem;
  }
}
</style>
